import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { PaymentMethodId } from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { PaymentProviderTransactionDetails } from 'Services/Api/_HumanWritten/PaymentService';

/**
 * All mutable UI state for a payment flow. This is the "view model" that
 * components read and write during a single payment interaction.
 */
interface PaymentUIState {
	paymentMethod: PaymentMethodId | undefined;
	tabIsLoading: boolean;
	paymentProcessing: boolean;
	disableContinueButton: boolean;
	totalPrice: number | null;
	dynamicSurcharge: number | null;
	message: string | null;
	showSurchargeModal: boolean;
	transactionDetails: PaymentProviderTransactionDetails | null;
	clearTerminalPolling?: () => Promise<void>;

	/**
	 * Monotonically increasing counter. Increment via requestPriceRefresh()
	 * to trigger a price re-fetch. Replaces the old boolean toggle which
	 * would cancel out if fired twice in the same React batch.
	 */
	priceRefreshCounter: number;

	/**
	 * Monotonically increasing counter. Increment via requestRecalculation()
	 * to trigger a full price recalculation. Replaces the old boolean toggle.
	 */
	recalculationCounter: number;
}

interface PaymentUIActions {
	setPaymentMethod: (method?: PaymentMethodId) => void;
	setTabIsLoading: (loading: boolean) => void;
	setPaymentProcessing: (processing: boolean) => void;
	setDisableContinueButton: (disabled: boolean) => void;
	setTotalPrice: (price: number | null) => void;
	setDynamicSurcharge: (surcharge: number | null) => void;
	updatePricing: (totalPrice: number, surcharge: number) => void;
	setShowSurchargeModal: (show: boolean) => void;
	setMessage: (message: string | null) => void;
	setTransactionDetails: (details: PaymentProviderTransactionDetails | null) => void;
	setClearTerminalPolling: (callback?: () => Promise<void>) => void;

	/**
	 * Set an error message and reset loading state. Unlike the old implementation,
	 * passing null clears the error instead of setting a generic message.
	 */
	handleError: (errorMessage: string | null) => void;

	/** Reset loading/processing flags without touching the error message. */
	handleProviderNotLoaded: () => void;

	/** Show surcharge modal and trigger a price refresh (and optionally recalculation). */
	handleSurchargeUpdate: (refresh?: boolean) => void;

	/** Increment priceRefreshCounter to trigger a price re-fetch. */
	requestPriceRefresh: () => void;

	/** Increment recalculationCounter to trigger a full recalculation. */
	requestRecalculation: () => void;

	/** Reset all mutable UI state to initial values. */
	resetUIState: () => void;
}

export interface PaymentUIContextValue extends PaymentUIState, PaymentUIActions {
	totalPriceWithSurcharge: number;
	hasError: boolean;
	canProceed: boolean;
}

const initialState: PaymentUIState = {
	paymentMethod: undefined,
	tabIsLoading: false,
	paymentProcessing: false,
	disableContinueButton: false,
	totalPrice: null,
	dynamicSurcharge: null,
	message: null,
	showSurchargeModal: false,
	transactionDetails: null,
	clearTerminalPolling: undefined,
	priceRefreshCounter: 0,
	recalculationCounter: 0,
};

const PaymentUIContext = createContext<PaymentUIContextValue | null>(null);

interface PaymentUIProviderProps {
	/** Optional initial payment method (e.g. 'terminal-option' for check-in). */
	initialPaymentMethod?: PaymentMethodId;
	/** Whether the flow is in check-in mode — affects canProceed logic. */
	inCheckIn?: boolean;
	children: ReactNode;
}

export function PaymentUIProvider({ initialPaymentMethod, inCheckIn = false, children }: PaymentUIProviderProps) {
	const [state, setState] = useState<PaymentUIState>(() => ({
		...initialState,
		paymentMethod: initialPaymentMethod,
	}));

	const updateState = useCallback((updates: Partial<PaymentUIState>) => {
		setState(prev => ({ ...prev, ...updates }));
	}, []);

	// --- Actions ---

	const setPaymentMethod = useCallback(
		(paymentMethod?: PaymentMethodId) => updateState({ paymentMethod }),
		[updateState],
	);
	const setTabIsLoading = useCallback((tabIsLoading: boolean) => updateState({ tabIsLoading }), [updateState]);
	const setPaymentProcessing = useCallback(
		(paymentProcessing: boolean) => updateState({ paymentProcessing }),
		[updateState],
	);
	const setDisableContinueButton = useCallback(
		(disableContinueButton: boolean) => updateState({ disableContinueButton }),
		[updateState],
	);
	const setTotalPrice = useCallback((totalPrice: number | null) => updateState({ totalPrice }), [updateState]);
	const setDynamicSurcharge = useCallback(
		(dynamicSurcharge: number | null) => updateState({ dynamicSurcharge }),
		[updateState],
	);
	const updatePricing = useCallback(
		(totalPrice: number, surcharge: number) => updateState({ totalPrice, dynamicSurcharge: surcharge }),
		[updateState],
	);
	const setShowSurchargeModal = useCallback(
		(showSurchargeModal: boolean) => updateState({ showSurchargeModal }),
		[updateState],
	);
	const setMessage = useCallback((message: string | null) => updateState({ message }), [updateState]);
	const setTransactionDetails = useCallback(
		(transactionDetails: PaymentProviderTransactionDetails | null) => updateState({ transactionDetails }),
		[updateState],
	);
	const setClearTerminalPolling = useCallback(
		(clearTerminalPolling?: () => Promise<void>) => updateState({ clearTerminalPolling }),
		[updateState],
	);

	const handleError = useCallback(
		(errorMessage: string | null) => {
			updateState({
				message: errorMessage,
				tabIsLoading: false,
				paymentProcessing: false,
				showSurchargeModal: false,
			});
		},
		[updateState],
	);

	const handleProviderNotLoaded = useCallback(
		() =>
			updateState({
				tabIsLoading: false,
				paymentProcessing: false,
			}),
		[updateState],
	);

	const handleSurchargeUpdate = useCallback((refresh = false) => {
		setState(prev => ({
			...prev,
			showSurchargeModal: true,
			priceRefreshCounter: prev.priceRefreshCounter + 1,
			...(refresh && {
				recalculationCounter: prev.recalculationCounter + 1,
			}),
		}));
	}, []);

	const requestPriceRefresh = useCallback(() => {
		setState(prev => ({ ...prev, priceRefreshCounter: prev.priceRefreshCounter + 1 }));
	}, []);

	const requestRecalculation = useCallback(() => {
		setState(prev => ({ ...prev, recalculationCounter: prev.recalculationCounter + 1 }));
	}, []);

	const resetUIState = useCallback(() => {
		setState(prev => ({
			...initialState,
			// Preserve the initial payment method when resetting
			paymentMethod: prev.paymentMethod,
		}));
	}, []);

	// --- Computed ---

	const totalPriceWithSurcharge = (state.totalPrice ?? 0) + (state.dynamicSurcharge ?? 0);
	const hasError = state.message !== null;
	const canProceed = (() => {
		if (!state.paymentMethod) return false;
		if (inCheckIn) return true;
		return !state.disableContinueButton;
	})();

	const contextValue: PaymentUIContextValue = {
		...state,
		setPaymentMethod,
		setTabIsLoading,
		setPaymentProcessing,
		setDisableContinueButton,
		setTotalPrice,
		setDynamicSurcharge,
		updatePricing,
		setShowSurchargeModal,
		setMessage,
		setTransactionDetails,
		setClearTerminalPolling,
		handleError,
		handleProviderNotLoaded,
		handleSurchargeUpdate,
		requestPriceRefresh,
		requestRecalculation,
		resetUIState,
		totalPriceWithSurcharge,
		hasError,
		canProceed,
	};

	return <PaymentUIContext.Provider value={contextValue}>{children}</PaymentUIContext.Provider>;
}

/**
 * Read and mutate payment UI state. Throws if called outside a
 * <PaymentUIProvider>.
 */
export function usePaymentUI(): PaymentUIContextValue {
	const context = useContext(PaymentUIContext);
	if (!context) {
		throw new Error('usePaymentUI must be used within a PaymentUIProvider');
	}
	return context;
}
